import * as amplitude from '@amplitude/analytics-browser';
import { sentry } from './sentry';

// Initialize Amplitude with your API key
process.env.REACT_APP_AMPLITUDE_API_KEY && amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY!, {
    defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
    },
});

// Event types
export enum AnalyticsEvent {
    // Page tracking
    VIEW_PAGE = 'view_page',
    
    // Content views
    VIEW_PODCAST = 'view_podcast',
    VIEW_SPONSOR = 'view_sponsor',
    VIEW_PUBLIC_REPORT = 'view_public_report',
    
    // Search and filter actions
    SEARCH_PODCASTS = 'search_podcasts',
    SEARCH_SPONSORS = 'search_sponsors',
    FILTER_PODCASTS = 'filter_podcasts',
    FILTER_SPONSORS = 'filter_sponsors',
    
    // Navigation clicks
    CLICK_DASHBOARD = 'click_dashboard',
    CLICK_PODCAST_LINK = 'click_podcast_link',
    CLICK_SPONSOR_LINK = 'click_sponsor_link',
    SORT_PODCASTS = 'sort_podcasts',
    CLICK_SPONSOR_EPISODES = 'click_sponsor_episodes',
    
    // Authentication events
    USER_SIGNIN = 'user_signin',
    USER_SIGNUP = 'user_signup',
    USER_SIGNOUT = 'user_signout',
    
    // Data export
    DOWNLOAD_CSV = 'download_csv',
    
    // Elections-specific events
    VIEW_ELECTIONS_DATA = 'view_elections_data',
    FILTER_ELECTIONS_DATA = 'filter_elections_data',
    VIEW_CANDIDATE_MENTIONS = 'view_candidate_mentions',
    VIEW_TOPIC_MENTIONS = 'view_topic_mentions',
    CHANGE_DATE_RANGE = 'change_date_range',
    TOGGLE_RADIO_FILTER = 'toggle_radio_filter',

    // Chat
    CHAT_ENTITY_LINK_CLICK = 'chat_entity_link_click',
    CHAT_LINK_CLICK = 'chat_link_click',
    CHAT_COLLECTION_LINK_CLICK = 'chat_collection_link_click',
    CHAT_VERIFY_EXPORT_CLICK = 'chat_verify_export_click',
    CHAT_MEDIA_KIT_CLICK = 'chat_media_kit_click',
    CHAT_COLLECTION_SAVED = 'chat_collection_saved',
    CHAT_STOP_GENERATION = 'chat_stop_generation',
}

// Analytics service
export const analytics = {
    // Track a simple event
    track: (eventName: AnalyticsEvent, properties?: Record<string, any>) => {
        try {
            amplitude.track(eventName, properties);
            // Add breadcrumb to Sentry for debugging
            sentry.addBreadcrumb({
                message: `Analytics event: ${eventName}`,
                category: 'analytics',
                level: 'info',
                data: properties,
            });
        } catch (error) {
            // Capture analytics errors in Sentry
            sentry.captureException(error as Error, {
                context: 'analytics_tracking',
                eventName,
                properties,
            });
        }
    },

    /** Amplitude `userId` is the first argument; Podscope auth uses normalized email (fallback: mongo `_id`). */
    identify: (userId: string, properties?: Record<string, any>) => {
        amplitude.setUserId(userId);
        if (properties) {
            const identify = new amplitude.Identify();
            Object.entries(properties).forEach(([key, value]) => {
                identify.set(key, value);
            });
            amplitude.identify(identify);
        }
    },

    // Reset the user
    reset: () => {
        amplitude.reset();
    },
};

export default analytics; 